import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react'
import { Alert, Button, Checkbox, Drawer, Form, Input, Modal, Radio, Segmented, Select, Skeleton, Space, Tooltip } from 'antd'
import type { FormInstance } from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import type {
  McpCatalog,
  McpCatalogEntry,
  McpPlaybook,
  McpPlaybookDraft,
  McpPlaybookInput,
  McpPlaybookStep,
  McpStepQuery,
} from '@/types/mcp'
import { useIsSavingMcpPlaybook, useMcpCatalog, useMcpPlaybook, useSaveMcpPlaybookMutation } from '../hooks'
import { brokenReferences, canonicalSteps, defaultTitle, linkProblems, moveItem, queryDimensions, queryFilters, queryProblems, stepIdFrom, stepsUsing } from './query'
import StepQueryEditor, { type EarlierQueryStep } from './StepQueryEditor'
import { catalogEntries, insertAt, matchesSearch, SCOPE_AUDIENCE, unusedInputs } from './text'

export type PlaybookFormMode = { kind: 'new' } | { kind: 'edit'; id: string } | { kind: 'copy'; id: string }

type FormValues = {
  name: string
  when?: string
  description?: string
  inputs?: McpPlaybookInput[]
  steps: McpPlaybookStep[]
  audience?: 'personal' | 'firm'
}

const MAX_INPUTS = 5
const MAX_STEPS = 25
const INPUT_NAME = /^[A-Za-z][A-Za-z0-9 _]{0,39}$/

function initialValues(mode: PlaybookFormMode, source: McpPlaybook | undefined): FormValues {
  if (!source || mode.kind === 'new') return { name: '', inputs: [], steps: [{ text: '' }], audience: 'personal' }
  return {
    name: mode.kind === 'copy' ? `${source.name} (copy)` : source.name,
    when: source.when,
    description: source.description,
    inputs: source.inputs,
    steps: source.steps.length ? canonicalSteps(source.steps) : [{ text: '' }],
    audience: 'personal',
  }
}

function toDraft(values: FormValues): McpPlaybookDraft {
  const trim = (value: string | undefined) => (value ?? '').trim()
  return {
    name: trim(values.name),
    when: trim(values.when),
    description: trim(values.description),
    inputs: (values.inputs ?? [])
      .map((input) => ({ name: trim(input?.name), description: trim(input?.description), workedOut: Boolean(input?.workedOut) }))
      .filter((input) => input.name),
    steps: values.steps.map((step) => ({ ...step, text: trim(step?.text) })).filter((step) => step.text),
  }
}

const stepsOf = (form: FormInstance<FormValues>) => (form.getFieldValue('steps') ?? []) as McpPlaybookStep[]

type PlaybookFormProps = {
  clientId: string
  mode: PlaybookFormMode | null
  isAdmin: boolean
  onClose: () => void
}

export default function PlaybookForm({ clientId, mode, isAdmin, onClose }: PlaybookFormProps) {
  const sourceId = mode && mode.kind !== 'new' ? mode.id : null
  const source = useMcpPlaybook(clientId, sourceId)
  const saving = useIsSavingMcpPlaybook(clientId)
  const hasChanges = useRef<() => boolean>(() => false)
  const fetched = !sourceId || source.isFetchedAfterMount

  const requestClose = () => {
    if (saving) return
    if (!hasChanges.current()) {
      onClose()
      return
    }
    Modal.confirm({
      title: 'Discard your changes?',
      content: 'Your changes to this playbook haven’t been saved.',
      okText: 'Discard',
      okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: onClose,
    })
  }

  return (
    <Drawer
      open={Boolean(mode)}
      onClose={requestClose}
      width={760}
      destroyOnHidden
      title={!mode ? '' : mode.kind === 'edit' ? 'Edit playbook' : mode.kind === 'copy' ? 'Copy playbook' : 'New playbook'}
    >
      {mode && !fetched && <Skeleton active paragraph={{ rows: 10 }} />}
      {mode && sourceId && fetched && source.isError && (
        <Alert
          type={source.data ? 'warning' : 'error'}
          showIcon
          className={source.data ? 'mb-4' : undefined}
          message={
            source.data
              ? 'Couldn’t check for newer changes to this playbook.'
              : source.error instanceof Error
                ? source.error.message
                : 'Couldn’t load this playbook.'
          }
        />
      )}
      {mode && fetched && (!sourceId || source.data) && (
        <PlaybookFormBody
          key={`${mode.kind}-${sourceId ?? 'new'}`}
          clientId={clientId}
          mode={mode}
          source={source.data}
          isAdmin={isAdmin}
          hasChanges={hasChanges}
          onCancel={requestClose}
          onDone={onClose}
          fetchLatest={() => source.refetch().then((latest) => latest.data)}
        />
      )}
    </Drawer>
  )
}

type BodyProps = {
  clientId: string
  mode: PlaybookFormMode
  source: McpPlaybook | undefined
  isAdmin: boolean
  hasChanges: MutableRefObject<() => boolean>
  onCancel: () => void
  onDone: () => void
  fetchLatest: () => Promise<McpPlaybook | undefined>
}

function PlaybookFormBody({ clientId, mode, source, isAdmin, hasChanges, onCancel, onDone, fetchLatest }: BodyProps) {
  const { addToast } = useToast()
  const [form] = Form.useForm<FormValues>()
  const save = useSaveMcpPlaybookMutation(clientId)
  const catalog = useMcpCatalog(clientId)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [pickerSearch, setPickerSearch] = useState('')
  const [initial] = useState(() => initialValues(mode, source))
  const [openedAt] = useState(source?.updatedAt ?? null)
  const [checkingLatest, setCheckingLatest] = useState(false)
  // Where the measure picker inserts: the instruction last edited and its selection.
  const cursor = useRef<{ index: number; start: number; end: number } | null>(null)
  // A query set aside by switching a step to Instruction, so switching back restores it.
  const setAside = useRef(new Map<number, McpStepQuery>())
  const editedTitles = useRef(new Set<number>())

  useEffect(() => {
    const snapshot = (values: FormValues) => JSON.stringify([toDraft(values), values.audience ?? 'personal'])
    const initialSnapshot = snapshot(initial)
    hasChanges.current = () => snapshot(form.getFieldsValue(true) as FormValues) !== initialSnapshot
    return () => {
      hasChanges.current = () => false
    }
  }, [form, hasChanges, initial])

  const isEdit = mode.kind === 'edit'
  const editingStarter = isEdit && source?.scope === 'starter'

  const entries = useMemo(() => catalogEntries(catalog.data), [catalog.data])

  const pickerOptions = useMemo(() => {
    const toOptions = (options: McpCatalogEntry[]) =>
      options
        .filter((entry) => matchesSearch(entry, pickerSearch))
        .map((entry) => ({
          value: entry.id,
          label: (
            <div className="flex items-baseline justify-between gap-3">
              <span>{entry.label}</span>
              <span className="text-xs text-gray-400">{entry.id}</span>
            </div>
          ),
        }))
    return [
      { label: 'Measures', options: toOptions(catalog.data?.measures ?? []) },
      { label: 'Dimensions', options: toOptions(catalog.data?.dimensions ?? []) },
    ].filter((group) => group.options.length > 0)
  }, [catalog.data, pickerSearch])

  const rememberCursor = (index: number) => (event: { currentTarget: HTMLTextAreaElement }) => {
    cursor.current = { index, start: event.currentTarget.selectionStart, end: event.currentTarget.selectionEnd }
  }

  const insertId = (id: string) => {
    const steps = stepsOf(form)
    const lastInstruction = steps.map((step) => !step?.query).lastIndexOf(true)
    const remembered = cursor.current && !steps[cursor.current.index]?.query ? cursor.current : null
    const fallbackText = steps[lastInstruction]?.text ?? ''
    const target = remembered ?? (lastInstruction >= 0 ? { index: lastInstruction, start: fallbackText.length, end: fallbackText.length } : null)
    if (!target) return
    const text = steps[target.index]?.text ?? ''
    const start = Math.min(target.start, text.length)
    form.setFieldValue(['steps', target.index, 'text'], insertAt(text, start, Math.min(target.end, text.length), id))
    cursor.current = { index: target.index, start: start + id.length, end: start + id.length }
    setPickerSearch('')
  }

  const submit = async (draft: McpPlaybookDraft, audience: 'personal' | 'firm') => {
    setSaveError(null)
    try {
      const saved = await save.mutateAsync(
        isEdit && source ? { id: source.id, playbook: draft } : { scope: isAdmin ? audience : 'personal', playbook: draft }
      )
      addToast(`Saved. Ask your AI assistant: run ${saved.name || draft.name}`, 'success')
      onDone()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Couldn’t save this playbook.')
    }
  }

  // The API takes the last save with no version check, so this is the only warning of replacing someone's edit.
  const submitUnlessChanged = async (draft: McpPlaybookDraft, audience: 'personal' | 'firm') => {
    if (!isEdit) return submit(draft, audience)
    setCheckingLatest(true)
    const latest = await fetchLatest()
    setCheckingLatest(false)
    if ((latest?.updatedAt ?? null) === openedAt) return submit(draft, audience)
    Modal.confirm({
      title: `${latest?.editedBy || 'Someone'} changed this playbook since you opened it`,
      content: 'Saving now replaces their changes.',
      okText: 'Save anyway',
      cancelText: 'Go back',
      onOk: () => submit(draft, audience),
    })
  }

  const usersMessage = (users: number[]) =>
    users.length === 1 ? `Step ${users[0]} takes values from this step` : `Steps ${users.join(', ')} take values from this step`

  // Dropping a step's id would leave a later step's valuesFrom pointing at nothing, which the API refuses.
  const confirmDropStep = (index: number, okText: string, drop: () => void) => {
    const steps = stepsOf(form)
    const id = steps[index]?.id
    const users = id ? stepsUsing(steps, id) : []
    const run = () => {
      drop()
      cursor.current = null
    }
    if (users.length === 0) {
      run()
      return
    }
    Modal.confirm({
      title: usersMessage(users),
      content: `Change ${users.length === 1 ? 'that step' : 'those steps'} too, or the playbook won’t save.`,
      okText,
      cancelText: 'Go back',
      onOk: run,
    })
  }

  const requestMove = (from: number, to: number, move: (from: number, to: number) => void) => {
    const steps = stepsOf(form)
    const run = () => {
      move(from, to)
      cursor.current = null
    }
    if (brokenReferences(moveItem(steps, from, to)).length <= brokenReferences(steps).length) {
      run()
      return
    }
    const moved = steps[from]
    const users = moved?.id ? stepsUsing(steps, moved.id) : []
    const sources = queryFilters(moved?.query ?? {})
      .flatMap((filter) => (filter['valuesFrom'] ? [steps.findIndex((step) => step.id === filter['valuesFrom']) + 1] : []))
      .filter((number) => number > 0)
    Modal.confirm({
      title: users.length ? usersMessage(users) : `This step takes values from step ${sources[0] ?? ''}`,
      content: users.length
        ? `Change ${users.length === 1 ? 'that step' : 'those steps'} too, or the playbook won’t save.`
        : 'Change this step too, or the playbook won’t save.',
      okText: 'Move anyway',
      cancelText: 'Go back',
      onOk: run,
    })
  }

  const toGetData = (index: number, key: number) => {
    const steps = stepsOf(form)
    const query = setAside.current.get(key) ?? {}
    form.setFieldValue(['steps', index, 'query'], query)
    if (!(steps[index]?.text ?? '').trim()) form.setFieldValue(['steps', index, 'text'], defaultTitle(query, steps, entries))
  }

  const toInstruction = (index: number, key: number) =>
    confirmDropStep(index, 'Switch to Instruction', () => {
      const step = stepsOf(form)[index]
      if (step?.query) setAside.current.set(key, step.query)
      form.setFieldValue(['steps', index], { text: step?.text ?? '' })
    })

  // Keeps the title following the settings until the user writes their own.
  const followTitle = (index: number, previous: McpStepQuery, next: McpStepQuery) => {
    const steps = stepsOf(form)
    const text = steps[index]?.text ?? ''
    if (!text.trim() || text === defaultTitle(previous, steps, entries)) {
      form.setFieldValue(['steps', index, 'text'], defaultTitle(next, steps, entries))
    }
  }

  const takeValuesFrom = (stepIndex: number) => {
    const steps = stepsOf(form)
    const existing = steps[stepIndex]?.id
    if (existing) return existing
    const id = stepIdFrom(
      steps[stepIndex]?.text ?? '',
      steps.flatMap((step) => (step?.id ? [step.id] : []))
    )
    form.setFieldValue(['steps', stepIndex, 'id'], id)
    return id
  }

  const handleSave = async () => {
    try {
      await form.validateFields()
    } catch {
      return
    }
    const values = form.getFieldsValue(true) as FormValues
    const draft = toDraft(values)
    const unused = unusedInputs(
      draft.inputs.map((input) => input.name),
      draft.steps
    )
    if (unused.length === 0) return submitUnlessChanged(draft, values.audience ?? 'personal')
    Modal.confirm({
      title: unused.length === 1 ? `“${unused[0]}” isn’t used in any step` : 'Some inputs aren’t used in any step',
      content: `Your AI assistant will ask for ${unused.map((name) => `“${name}”`).join(', ')} but no step uses it. Put {${unused[0]}} in a step where it belongs, or save anyway.`,
      okText: 'Save anyway',
      cancelText: 'Go back',
      onOk: () => submitUnlessChanged(draft, values.audience ?? 'personal'),
    })
  }

  const stepRule = { required: true, whitespace: true, message: 'Write the step, or remove it.' }

  return (
    <Space orientation="vertical" size={16} className="w-full">
      {editingStarter && (
        <Alert
          type="info"
          showIcon
          message="Everyone at the firm will see your version instead of the starter. You can go back to the starter later."
        />
      )}
      {saveError && <Alert type="error" showIcon message={saveError} />}
      {catalog.isError && (
        <Alert
          type="warning"
          showIcon
          message="Couldn’t load the measures and dimensions, so Get data steps can’t be built or changed."
          action={
            <Button size="small" loading={catalog.isFetching} onClick={() => void catalog.refetch()}>
              Try again
            </Button>
          }
        />
      )}

      <Form form={form} layout="vertical" initialValues={initial} requiredMark>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, whitespace: true, message: 'Give the playbook a name.' }, { max: 80 }]}
        >
          <Input maxLength={80} showCount placeholder="e.g. Friday debtors call list" />
        </Form.Item>
        <div className="grid gap-x-4 md:grid-cols-2">
          <Form.Item name="when" label="When" rules={[{ max: 80 }]}>
            <Input maxLength={80} placeholder="e.g. Weekly, Fridays" />
          </Form.Item>
          <Form.Item label="Who sees it">
            {isEdit ? (
              <span className="text-gray-700">{source ? SCOPE_AUDIENCE[source.scope] : ''}</span>
            ) : isAdmin ? (
              <Form.Item name="audience" noStyle>
                <Radio.Group
                  options={[
                    { value: 'personal', label: 'Only me' },
                    { value: 'firm', label: 'Everyone at the firm' },
                  ]}
                />
              </Form.Item>
            ) : (
              <span className="text-gray-700">Only me</span>
            )}
          </Form.Item>
        </div>
        <Form.Item name="description" label="Description" rules={[{ max: 300 }]}>
          <Input.TextArea maxLength={300} showCount autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>

        <Form.List name="inputs">
          {(fields, { add, remove }) => (
            <div className="mb-6">
              <div className="mb-2 flex items-baseline justify-between">
                <span className="font-medium text-gray-900">Asks for</span>
                <span className="text-xs text-gray-500">Use one in a step as {'{name}'}, e.g. “searching for {'{client}'}”.</span>
              </div>
              {fields.map((field) => (
                <div key={field.key} className="mb-3 rounded-lg border border-gray-200 px-3 pt-3">
                  <div className="flex items-start gap-2">
                    <Form.Item
                      name={[field.name, 'name']}
                      className="!mb-2 w-48"
                      dependencies={fields.filter((other) => other.name !== field.name).map((other) => ['inputs', other.name, 'name'])}
                      rules={[
                        { required: true, message: 'Name the input.' },
                        { pattern: INPUT_NAME, message: 'A letter first, then letters, digits, spaces or _ (up to 40).' },
                        {
                          validator: async (_, name: string | undefined) => {
                            const key = (name ?? '').trim().toLowerCase()
                            const inputs = (form.getFieldValue('inputs') ?? []) as McpPlaybookInput[]
                            const clash = inputs.some((input, at) => at !== field.name && (input?.name ?? '').trim().toLowerCase() === key)
                            if (key && clash) throw new Error('Another input has this name.')
                          },
                        },
                      ]}
                    >
                      <Input placeholder="client" aria-label="Input name" />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const workedOut = Boolean(form.getFieldValue(['inputs', field.name, 'workedOut']))
                        return (
                          <Form.Item
                            name={[field.name, 'description']}
                            className="!mb-2 grow"
                            dependencies={[['inputs', field.name, 'workedOut']]}
                            rules={[
                              { max: 200 },
                              ...(workedOut ? [{ required: true, whitespace: true, message: 'Say how your AI assistant works it out.' }] : []),
                            ]}
                          >
                            <Input
                              maxLength={200}
                              placeholder={workedOut ? 'How it’s worked out, e.g. from today’s date' : 'What to ask for, e.g. the client’s name'}
                              aria-label={workedOut ? 'How it’s worked out' : 'Input description'}
                            />
                          </Form.Item>
                        )
                      }}
                    </Form.Item>
                    <Tooltip title="Remove input">
                      <Button icon={<DeleteOutlined />} aria-label="Remove input" onClick={() => remove(field.name)} />
                    </Tooltip>
                  </div>
                  <Form.Item name={[field.name, 'workedOut']} valuePropName="checked" className="!mb-2">
                    <Checkbox>Your AI assistant works it out</Checkbox>
                  </Form.Item>
                </div>
              ))}
              {fields.length < MAX_INPUTS && (
                <Button icon={<PlusOutlined />} onClick={() => add({ name: '', description: '', workedOut: false })}>
                  Add an input
                </Button>
              )}
            </div>
          )}
        </Form.List>

        <Form.List
          name="steps"
          rules={[
            {
              validator: async (_, steps: McpPlaybookStep[] | undefined) => {
                if (!steps || steps.length === 0) throw new Error('A playbook needs at least one step.')
              },
            },
          ]}
        >
          {(fields, { add, remove, move }, { errors }) => (
            <div className="mb-6">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-gray-900">Steps</span>
                <Form.Item noStyle shouldUpdate>
                  {() => (
                    <Select
                      showSearch
                      value={null}
                      placeholder="Insert a measure or dimension"
                      aria-label="Insert a measure or dimension"
                      className="w-80"
                      loading={catalog.isLoading}
                      disabled={!catalog.data || !stepsOf(form).some((step) => !step?.query)}
                      searchValue={pickerSearch}
                      onSearch={setPickerSearch}
                      filterOption={false}
                      options={pickerOptions}
                      onSelect={(id: string | null) => id && insertId(id)}
                      notFoundContent="Nothing matches."
                    />
                  )}
                </Form.Item>
              </div>
              <ol className="m-0 list-none space-y-4 p-0">
                {fields.map((field, index) => (
                  <li key={field.key} className="flex items-start gap-2">
                    <span className="mt-1 w-6 shrink-0 text-right text-sm font-semibold text-gray-500">{index + 1}.</span>
                    <div className="min-w-0 grow">
                      <Form.Item noStyle shouldUpdate>
                        {() => {
                          const steps = stepsOf(form)
                          const step = steps[field.name]
                          const query = step?.query
                          return (
                            <div className="flex flex-col gap-2.5">
                              <Segmented
                                size="small"
                                className="self-start"
                                aria-label={`Step ${index + 1} type`}
                                value={query ? 'data' : 'instruction'}
                                options={[
                                  { value: 'instruction', label: 'Instruction' },
                                  { value: 'data', label: 'Get data' },
                                ]}
                                onChange={(kind) =>
                                  kind === 'data' ? toGetData(field.name, field.key) : toInstruction(field.name, field.key)
                                }
                              />
                              {query ? (
                                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3.5">
                                  <Form.Item
                                    name={[field.name, 'text']}
                                    label="Title"
                                    className="!mb-3"
                                    rules={[stepRule, { max: 1000 }]}
                                    extra={
                                      <TitleHint
                                        text={step?.text ?? ''}
                                        suggested={catalog.data ? defaultTitle(query, steps, entries) : ''}
                                        edited={editedTitles.current.has(field.key)}
                                        onUse={(title) => {
                                          editedTitles.current.delete(field.key)
                                          form.setFieldValue(['steps', field.name, 'text'], title)
                                        }}
                                      />
                                    }
                                  >
                                    <Input
                                      maxLength={1000}
                                      aria-label={`Step ${index + 1} title`}
                                      onChange={() => editedTitles.current.add(field.key)}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'query']}
                                    className="!mb-0"
                                    rules={[
                                      {
                                        validator: async (_, value: McpStepQuery | undefined) => {
                                          const label = (id: string) => entries.get(id)?.label ?? id
                                          const problem =
                                            queryProblems(value ?? {}, entries)[0] ?? linkProblems(stepsOf(form), field.name, label)[0]
                                          if (problem) throw new Error(problem)
                                        },
                                      },
                                    ]}
                                  >
                                    <QueryField
                                      clientId={clientId}
                                      stepNumber={index + 1}
                                      catalog={catalog.data}
                                      catalogLoading={catalog.isLoading}
                                      entries={entries}
                                      earlierSteps={earlierQuerySteps(steps, field.name)}
                                      onTakeValuesFrom={takeValuesFrom}
                                      onQueryChange={(previous, next) => followTitle(field.name, previous, next)}
                                    />
                                  </Form.Item>
                                </div>
                              ) : (
                                <Form.Item name={[field.name, 'text']} className="!mb-0" rules={[stepRule, { max: 1000 }]}>
                                  <Input.TextArea
                                    autoSize={{ minRows: 2, maxRows: 8 }}
                                    maxLength={1000}
                                    aria-label={`Step ${index + 1}`}
                                    onSelect={rememberCursor(index)}
                                    onKeyUp={rememberCursor(index)}
                                    onClick={rememberCursor(index)}
                                  />
                                </Form.Item>
                              )}
                            </div>
                          )
                        }}
                      </Form.Item>
                    </div>
                    <Space.Compact orientation="vertical">
                      <Tooltip title="Move up" placement="left">
                        <Button
                          size="small"
                          icon={<ArrowUpOutlined />}
                          aria-label={`Move step ${index + 1} up`}
                          disabled={index === 0}
                          onClick={() => requestMove(index, index - 1, move)}
                        />
                      </Tooltip>
                      <Tooltip title="Move down" placement="left">
                        <Button
                          size="small"
                          icon={<ArrowDownOutlined />}
                          aria-label={`Move step ${index + 1} down`}
                          disabled={index === fields.length - 1}
                          onClick={() => requestMove(index, index + 1, move)}
                        />
                      </Tooltip>
                      <Tooltip title="Remove step" placement="left">
                        <Button
                          size="small"
                          icon={<DeleteOutlined />}
                          aria-label={`Remove step ${index + 1}`}
                          disabled={fields.length === 1}
                          onClick={() => confirmDropStep(field.name, 'Remove step', () => remove(field.name))}
                        />
                      </Tooltip>
                    </Space.Compact>
                  </li>
                ))}
              </ol>
              <Form.ErrorList errors={errors} />
              {fields.length < MAX_STEPS && (
                <Button className="mt-3" icon={<PlusOutlined />} onClick={() => add({ text: '' })}>
                  Add a step
                </Button>
              )}
            </div>
          )}
        </Form.List>
      </Form>

      <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" loading={save.isPending || checkingLatest} onClick={() => void handleSave()}>
          Save
        </Button>
      </div>
    </Space>
  )
}

function earlierQuerySteps(steps: McpPlaybookStep[], index: number): EarlierQueryStep[] {
  return steps.slice(0, index).flatMap((step, at) =>
    step?.query ? [{ index: at, ...(step.id ? { id: step.id } : {}), dimensions: queryDimensions(step.query) }] : []
  )
}

type QueryFieldProps = {
  clientId: string
  stepNumber: number
  catalog: McpCatalog | undefined
  catalogLoading: boolean
  entries: Map<string, McpCatalogEntry>
  earlierSteps: EarlierQueryStep[]
  onTakeValuesFrom: (stepIndex: number) => string
  onQueryChange: (previous: McpStepQuery, next: McpStepQuery) => void
  value?: McpStepQuery
  onChange?: (next: McpStepQuery) => void
}

/** Form.Item hands over the query before the change, so the title can tell whether it was still the default. */
function QueryField({ value, onChange, onQueryChange, ...editor }: QueryFieldProps) {
  return (
    <StepQueryEditor
      {...editor}
      value={value}
      onChange={(next) => {
        onQueryChange(value ?? {}, next)
        onChange?.(next)
      }}
    />
  )
}

type TitleHintProps = {
  text: string
  suggested: string
  /** Typed in this form; a title saved before (in your AI assistant, or a starter's) isn't. */
  edited: boolean
  onUse: (title: string) => void
}

function TitleHint({ text, suggested, edited, onUse }: TitleHintProps) {
  if (!suggested) return <span>Fills in as you choose what to get.</span>
  if (text === suggested) return <span>Filled in from your choices below. Change it if you like.</span>
  return (
    <span className="flex flex-wrap items-baseline gap-x-1.5">
      <span>{edited ? 'You changed this title.' : 'Suggested from your choices:'}</span>
      <Button type="link" size="small" className="!h-auto !p-0 !text-xs" onClick={() => onUse(suggested)}>
        Use “{suggested}”
      </Button>
    </span>
  )
}
